import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from '@/lib/mixpanel';

/**
 * Hook to automatically track page views
 */
export const useMixpanelPageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever location changes
    const pageName = location.pathname === '/' ? 'Home' : location.pathname.slice(1);
    analytics.trackPageView(pageName, {
      path: location.pathname,
      search: location.search,
    });
  }, [location]);
};

/**
 * Tracking functions for common FaceFable events
 */
export const trackFaceScore = (score: number, breakdown?: Record<string, number>) => {
  analytics.track('Face Score Completed', {
    score,
    ...breakdown,
  });
  analytics.incrementUserProperty('total_face_scans');
};

export const trackQuizCompleted = (quizType: string, score: number) => {
  analytics.track('Quiz Completed', {
    quiz_type: quizType,
    score,
  });
};

export const trackRoutineLogged = (routineType: 'morning' | 'evening') => {
  analytics.track('Routine Logged', {
    routine_type: routineType,
  });
  analytics.incrementUserProperty('total_routines_logged');
};

export const trackProductFeedback = (productName: string, feedbackType: string) => {
  analytics.track('Product Feedback Submitted', {
    product_name: productName,
    feedback_type: feedbackType,
  });
};

export const trackProgressStoryCreated = (isPublic: boolean) => {
  analytics.track('Progress Story Created', {
    is_public: isPublic,
  });
};

export const trackProfileUpdated = (fieldsUpdated: string[]) => {
  analytics.track('Profile Updated', {
    fields_updated: fieldsUpdated,
  });
};

export const trackPaymentEvent = (event: 'initiated' | 'completed' | 'failed', plan?: string) => {
  analytics.track(`Payment ${event}`, {
    plan,
  });
};

export const trackRecommendationEvent = (
  event: 'viewed' | 'accepted' | 'dismissed' | 'snoozed',
  recommendationId: string,
  ruleId: string,
  category: string
) => {
  analytics.track('Recommendation Event', {
    event,
    recommendation_id: recommendationId,
    rule_id: ruleId,
    category,
  });
};

export const trackModeratorAction = (
  action: 'reviewed_report' | 'resolved_report' | 'dismissed_report',
  reportId: string,
  status: string
) => {
  analytics.track('Moderator Action', {
    action,
    report_id: reportId,
    status,
  });
};

/**
 * Routine page tracking functions
 */
export const trackRoutinePageViewed = (activeTab: string) => {
  analytics.track('Routine Page Viewed', {
    active_tab: activeTab,
  });
};

export const trackRoutineStepCompleted = (
  productId: string,
  productName: string,
  routineType: 'morning' | 'evening',
  isCompleted: boolean
) => {
  analytics.track('Routine Step Toggled', {
    product_id: productId,
    product_name: productName,
    routine_type: routineType,
    is_completed: isCompleted,
  });
  
  if (isCompleted) {
    analytics.incrementUserProperty('total_routines_logged');
  }
};

export const trackWaterIntakeLogged = (amount: number, percentage: number) => {
  analytics.track('Water Intake Logged', {
    amount_ml: amount,
    percentage,
  });
  analytics.incrementUserProperty('total_water_logs');
};

export const trackRoutineProgressSync = (amProgress: number, pmProgress: number, waterProgress: number) => {
  analytics.track('Routine Progress Synced', {
    am_progress: amProgress,
    pm_progress: pmProgress,
    water_progress: waterProgress,
    overall_progress: Math.round((amProgress + pmProgress + waterProgress) / 3),
  });
};

export const trackRoutineTabChanged = (fromTab: string, toTab: string) => {
  analytics.track('Routine Tab Changed', {
    from_tab: fromTab,
    to_tab: toTab,
  });
};

export const trackRoutineReportViewed = (amGrade: string, pmGrade: string, amScore: number, pmScore: number) => {
  analytics.track('Routine Report Viewed', {
    am_grade: amGrade,
    pm_grade: pmGrade,
    am_score: amScore,
    pm_score: pmScore,
  });
};

export const trackIrritantExpanded = (irritantType: string) => {
  analytics.track('Irritant Expanded', {
    irritant_type: irritantType,
  });
};

export const trackRoutineRecheckClicked = (previousScore: number) => {
  analytics.track('Routine Recheck Clicked', {
    previous_score: previousScore,
  });
};

export const trackRoutineScoreCalculated = (data: {
  coverage_score: number;
  safety_score: number;
  consistency_score: number;
  final_score: number;
  grade: string;
  projected_gain: number;
  missing_steps: number;
  irritant_count: number;
  days_completed_last_7: number;
}) => {
  analytics.track('Routine Score Calculated', data);
};

// Routine IQ™ Events
export const trackRoutineIQCompleted = (score: number) => {
  analytics.track('routine_iq_completed', {
    score,
    timestamp: new Date().toISOString(),
  });
};

export const trackRoutineIQStarted = () => {
  analytics.track('routine_iq_started', {
    timestamp: new Date().toISOString(),
  });
};

// Routine Building Events
export const trackProductAddedFromRoutine = (
  productName: string,
  category: string,
  routineType: 'morning' | 'evening',
  wasPlaceholder: boolean = false
) => {
  analytics.track('Product Added From Routine', {
    product_name: productName,
    category,
    routine_type: routineType,
    was_placeholder: wasPlaceholder,
    timestamp: new Date().toISOString(),
  });
  analytics.incrementUserProperty('products_added_from_routine');
};

export const trackPlaceholderReplaced = (
  placeholderName: string,
  newProductName: string,
  category: string,
  routineType: 'morning' | 'evening'
) => {
  analytics.track('Placeholder Replaced', {
    placeholder_name: placeholderName,
    new_product_name: newProductName,
    category,
    routine_type: routineType,
    timestamp: new Date().toISOString(),
  });
  analytics.incrementUserProperty('placeholders_replaced');
};

export const trackQuickAddOpened = (
  category: string,
  routineType: 'morning' | 'evening'
) => {
  analytics.track('Quick Add Opened', {
    category,
    routine_type: routineType,
    timestamp: new Date().toISOString(),
  });
};

// Product page events
export const trackProductViewed = (data: {
  productId: string;
  productName: string;
  brand?: string;
  category?: string;
  source?: string;
}) => {
  analytics.track('Product Viewed', {
    product_id: data.productId,
    product_name: data.productName,
    brand: data.brand,
    category: data.category,
    source: data.source,
    timestamp: new Date().toISOString(),
  });
  analytics.incrementUserProperty('products_viewed');
};
